// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import { getCookie } from "@insite/client-framework/Common/Cookies";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { CartModel } from "@insite/client-framework/Types/ApiModels";

const getDataLayer = () => {
    if (!(window as any).dataLayer) {
        (window as any).dataLayer = [];
    }

    return (window as any).dataLayer;
};

export { getDataLayer };

export const CHECKOUT_STEPS = {
    CART: 0,
    ADDRESS: 1,
    SHIPPING: 2,
    PAYMENT: 3,
    REVIEW: 4,
};

/* eslint-disable */
(function (window) {
    (window as any)._GaCheckoutFunnel = (
        step: number,
        cart: CartModel,
        discount?: number,
        includeBrand?: boolean,
        includeCategory?: boolean,
    ) => {
        const dataLayer = getDataLayer();
        if (!dataLayer || !cart.orderSubTotal) {
            return;
        }

        const googleAnalyticsData = {
            ...(step === CHECKOUT_STEPS.ADDRESS && {
                event: "begin_checkout",
            }),
            ...(step === CHECKOUT_STEPS.SHIPPING && {
                event: "add_shipping_info",
                shipping_tier: cart.carrier?.description || "",
            }),
            ...(step === CHECKOUT_STEPS.PAYMENT && {
                event: "add_payment_info",
                payment_type: cart.paymentMethod?.description || "",
            }),
            items: cart?.cartLines?.map(cartLine => ({
                item_name: cartLine.shortDescription,
                item_id: cartLine.erpNumber,
                price: cartLine.pricing?.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                item_brand: includeBrand ? cartLine.brand?.name || "" : undefined,
                item_category: includeCategory ? cartLine.properties?.["category"] || "" : undefined,
                item_list_id: includeCategory ? cartLine.properties?.["categoryId"] || "" : undefined,
            })),
            discount: discount || 0,
            value: cart.orderSubTotal,
        };

        if (googleAnalyticsData.event !== "add_payment_info" || shouldPushToDataLayer(googleAnalyticsData)) {
            dataLayer.push(googleAnalyticsData);
        }
    };
})(typeof window != "undefined" ? window : {});
/* eslint-enable */

function shouldPushToDataLayer(googleAnalyticsData: any) {
    const dataLayer = getDataLayer();
    const lastEvent = dataLayer[dataLayer.length - 1];
    return lastEvent?.event !== "add_payment_info" || lastEvent.payment_type !== googleAnalyticsData.payment_type;
}

// Toolbar Nav Click Event
export const trackGaToolbarNavClickEvent = (label: string, impersonation: boolean) => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const googleAnalyticsData = {
        event: "tool_bar_nav",
        text: label,
        impersonation,
    };

    dataLayer.push(googleAnalyticsData);
};

export const trackAsImpersonation = (state: ApplicationState) => {
    const session = getSession(state);
    const isInternalCustomerServiceUser = session.properties?.isInternalCustomerServiceUser?.toLowerCase() === "true";
    return session.isSalesPerson || isInternalCustomerServiceUser || !!getCookie("ImpersonateBy");
};
